import { useEffect, useState, useCallback } from "react";
import { Bell, Megaphone, LayoutTemplate, History, Sparkles, Send, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const TABS = [
  { id: "broadcast", label: "Broadcast", icon: Megaphone },
  { id: "templates", label: "Templates", icon: LayoutTemplate },
  { id: "history", label: "History", icon: History },
];

function Broadcast() {
  const [form, setForm] = useState({ title: "", message: "", audience: "customers", send_email: false, email_subject: "", route: "/app/notifications" });
  const [aiBrief, setAiBrief] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const draft = async () => {
    if (!aiBrief.trim()) { toast.error("Describe what you want to announce first"); return; }
    setDrafting(true);
    try {
      const r = await api.post("/notifications/admin/ai-draft", { purpose: aiBrief, audience: form.audience, tone: "friendly" }, { timeout: 30000 });
      const { title, message, email_subject } = r.data || {};
      if (!title && !message) { toast.error("The assistant is busy — please try again in a moment"); return; }
      setForm((f) => ({ ...f, title: title || f.title, message: message || f.message, email_subject: email_subject || f.email_subject }));
      toast.success("Draft ready — review & edit before sending");
    } catch (e) { toast.error("The assistant timed out — please try again"); }
    finally { setDrafting(false); }
  };
  const send = async () => {
    if (!form.title.trim() || !form.message.trim()) { toast.error("Add a title and message"); return; }
    setSending(true);
    try {
      const r = await api.post("/notifications/admin/broadcast", form);
      toast.success(`Sent to ${r.data.recipients} — ${r.data.inapp_delivered} in-app, ${r.data.email_delivered} emails`);
      setForm({ title: "", message: "", audience: form.audience, send_email: false, email_subject: "", route: "/app/notifications" });
      setAiBrief("");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card-soft p-5">
        <p className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-violet-500" /> AI communication assistant</p>
        <p className="mb-3 text-xs text-slate-500">Describe your announcement and let AI draft on-brand copy. It never invents offers or dates.</p>
        <Textarea value={aiBrief} onChange={(e) => setAiBrief(e.target.value)} rows={3} data-testid="broadcast-ai-brief"
          placeholder="e.g. Announce that we now ship to 5 new cities, warm and welcoming tone" />
        <button onClick={draft} disabled={drafting} data-testid="broadcast-ai-draft-btn"
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:opacity-60">
          {drafting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft with AI
        </button>
      </div>

      <div className="card-soft space-y-3 p-5">
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Audience</label>
          <select value={form.audience} onChange={(e) => set("audience", e.target.value)} data-testid="broadcast-audience"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="customers">Customers</option>
            <option value="sellers">Sellers</option>
            <option value="all">Everyone</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Title</label>
          <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="broadcast-title" placeholder="Headline shown in the bell" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Message</label>
          <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={3} data-testid="broadcast-message" placeholder="What do you want to say?" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-slate-600">Deep link (route)</label>
          <Input value={form.route} onChange={(e) => set("route", e.target.value)} data-testid="broadcast-route" placeholder="/app/shop" />
        </div>
        <label className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm text-slate-600">
          Also send an email (only to consented recipients)
          <Switch checked={form.send_email} onCheckedChange={(v) => set("send_email", v)} data-testid="broadcast-send-email" />
        </label>
        {form.send_email && (
          <Input value={form.email_subject} onChange={(e) => set("email_subject", e.target.value)} data-testid="broadcast-email-subject" placeholder="Email subject (defaults to title)" />
        )}
        <button onClick={send} disabled={sending} data-testid="broadcast-send-btn"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient-bg py-2.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-60">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send broadcast
        </button>
      </div>
    </div>
  );
}

function Templates() {
  const [events, setEvents] = useState(null);
  const load = useCallback(() => {
    api.get("/notifications/admin/templates").then((r) => setEvents(r.data.events || [])).catch(() => setEvents([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (e, val) => {
    setEvents((es) => es.map((x) => x.id === e.id ? { ...x, enabled: val } : x));
    try { await api.put(`/notifications/admin/templates/${e.id}`, { enabled: val }); toast.success(`${e.label} ${val ? "enabled" : "disabled"}`); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); load(); }
  };
  if (events === null) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-white/70" />)}</div>;
  return (
    <div className="card-soft divide-y divide-slate-100" data-testid="notif-templates">
      {events.map((e) => (
        <div key={e.id} className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-sm font-medium text-slate-800">{e.label}</p>
            <p className="text-xs text-slate-400">{e.id} · {e.kind}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${e.enabled ? "text-emerald-600" : "text-slate-400"}`}>{e.enabled ? "On" : "Off"}</span>
            <Switch checked={e.enabled} onCheckedChange={(v) => toggle(e, v)} data-testid={`template-${e.id}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

function BroadcastHistory() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/notifications/admin/broadcasts").then((r) => setRows(r.data.broadcasts || [])).catch(() => setRows([])); }, []);
  if (rows === null) return <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="h-16 animate-pulse rounded-lg bg-white/70" />)}</div>;
  if (rows.length === 0) return <div className="card-soft p-8 text-center text-sm text-slate-500" data-testid="broadcast-history-empty">No broadcasts sent yet.</div>;
  return (
    <div className="space-y-3" data-testid="broadcast-history">
      {rows.map((b) => (
        <div key={b.broadcast_id} className="card-soft p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{b.title}</p>
              <p className="mt-0.5 text-sm text-slate-500">{b.message}</p>
            </div>
            <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium capitalize text-slate-600">{b.audience}</span>
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {b.recipients} recipients · {b.inapp_delivered} in-app · {b.email_delivered} emails · by {b.sent_by} · {new Date(b.created_at).toLocaleString()}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function AdminNotifications() {
  const [tab, setTab] = useState("broadcast");
  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader title="Communication Center" icon={Bell}
        subtitle="One engine for every marketplace alert — broadcast announcements, manage notification templates, and review delivery history." />
      <div className="mb-5 flex gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`comms-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition ${tab === t.id ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "broadcast" && <Broadcast />}
      {tab === "templates" && <Templates />}
      {tab === "history" && <BroadcastHistory />}
    </div>
  );
}
